'use client';

import { useState, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  BadgeMemoryStatus,
  BodyDefault,
  EndpointMemorySourceBadge,
  MetaDefault,
  Table,
  Tooltip,
  Button,
  Scroll,
  Input,
} from '@terra/ui-design-system';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { Brain01Icon, Add01Icon, Search01Icon } from '@terra/ui-icons';
import { EmptyState } from '../EmptyState';
import { useEndpointMemories } from '../../hooks/api/useEndpointMemories';
import { useEndpointMemoryActions } from '../../hooks/api/useEndpointMemoryActions';
import { isSyntheticMemoryRecordId } from '../../hooks/endpointMemoryOverlays';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import { EndpointMemoriesContentActions } from './EndpointMemoriesContentActions';
import { EndpointMemoriesDialogs } from './EndpointMemoriesDialogs';

type EndpointMemoriesContentProps = {
  applicationId: string | undefined;
  endpointId: string;
};

export function EndpointMemoriesContent({
  applicationId,
  endpointId,
}: EndpointMemoriesContentProps) {
  const dateFormatter = useDateFormatter();
  const t = useTranslations('memories.endpointMemories');
  const tMemories = useTranslations('memories');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMemory, setViewMemory] = useState<MemoryRecordResponse | null>(
    null,
  );
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingMemory, setEditingMemory] =
    useState<MemoryRecordResponse | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const { memories, totalCount, isLoading, refetch } = useEndpointMemories({
    applicationId,
    endpointId,
    searchTerm,
  });
  const {
    isSubmitting,
    createMemory,
    updateMemory,
    deleteMemory,
    isActionInProgress,
  } = useEndpointMemoryActions({
    applicationId,
    endpointId,
    onSuccess: refetch,
  });
  const hasActiveSearch = searchTerm.trim().length > 0;
  const showSearch = totalCount > 1 || hasActiveSearch;
  const openCreate = useCallback(() => {
    setEditingMemory(null);
    setEditorContent('');
    setEditorOpen(true);
  }, []);
  const openEdit = useCallback((memory: MemoryRecordResponse) => {
    setEditingMemory(memory);
    setEditorContent(memory.content);
    setEditorOpen(true);
  }, []);
  const handleView = useCallback((memory: MemoryRecordResponse) => {
    setViewMemory(memory);
    setIsViewDialogOpen(true);
  }, []);
  const handleSave = useCallback(async () => {
    const isRetryingRejected =
      editingMemory != null &&
      isSyntheticMemoryRecordId(editingMemory.recordId);
    const success =
      editingMemory && !isRetryingRejected
        ? await updateMemory(editingMemory.recordId, editorContent)
        : await createMemory(editorContent, {
            replaceRecordId: isRetryingRejected
              ? editingMemory.recordId
              : undefined,
          });

    if (success) {
      setEditorOpen(false);
      setEditingMemory(null);
      setEditorContent('');
    }
  }, [createMemory, editingMemory, editorContent, updateMemory]);
  const ActionButtonsRenderer = useCallback(
    ({ data: memory }: { data: MemoryRecordResponse | undefined }) => {
      if (!memory) {
        return null;
      }

      return (
        <EndpointMemoriesContentActions
          memory={memory}
          loading={isActionInProgress(memory.recordId)}
          onView={handleView}
          onEdit={openEdit}
          onDelete={(pendingMemory) => {
            void deleteMemory(pendingMemory);
          }}
        />
      );
    },
    [deleteMemory, handleView, isActionInProgress, openEdit],
  );
  const columnDefs = useMemo<ColDef<MemoryRecordResponse>[]>(
    () => [
      {
        headerName: t('columnContent'),
        field: 'content',
        flex: 1,
        minWidth: 300,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          if (!params.data) {
            return null;
          }

          return (
            <BodyDefault as="span" truncate title={params.data.content}>
              {params.data.content}
            </BodyDefault>
          );
        },
      },
      {
        headerName: tMemories('columnCreationMethod'),
        field: 'creationMethod',
        width: 120,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => (
          <EndpointMemorySourceBadge
            creationMethod={params.data?.creationMethod}
          />
        ),
      },
      {
        headerName: tMemories('columnStatus'),
        field: 'status',
        width: 150,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          const status = params.data?.status ?? 'active';
          const statusTooltip =
            status === 'rejected' && params.data?.creationMethod === 'system'
              ? tMemories('statusTooltip.rejectedSystem')
              : status === 'rejected' && params.data?.rejectionReason
                ? params.data.rejectionReason
                : tMemories(`statusTooltip.${status}`);

          return (
            <Tooltip title={statusTooltip}>
              <BadgeMemoryStatus status={status}>
                {tMemories(`status.${status}`)}
              </BadgeMemoryStatus>
            </Tooltip>
          );
        },
      },
      {
        headerName: t('columnSaved'),
        field: 'createdAt',
        width: 160,
        sortable: true,
        sort: 'desc',
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          if (!params.data?.createdAt) {
            return <MetaDefault color="fg.muted">-</MetaDefault>;
          }

          return (
            <MetaDefault color="fg.muted">
              {dateFormatter.format(new Date(params.data.createdAt))}
            </MetaDefault>
          );
        },
      },
      {
        headerName: t('columnActions'),
        width: 120,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => (
          <ActionButtonsRenderer data={params.data} />
        ),
        filter: false,
        sortable: false,
        resizable: false,
      },
    ],
    [dateFormatter, ActionButtonsRenderer, t, tMemories],
  );
  const showInitialLoading = isLoading && memories.length === 0;
  const showEmptyState = !isLoading && totalCount === 0 && !hasActiveSearch;
  const dialogs = (
    <EndpointMemoriesDialogs
      viewMemory={viewMemory}
      isViewDialogOpen={isViewDialogOpen}
      onViewDialogOpenChange={(open) => {
        setIsViewDialogOpen(open);
        if (!open) {
          setViewMemory(null);
        }
      }}
      editorOpen={editorOpen}
      editingMemory={editingMemory}
      editorContent={editorContent}
      isSubmitting={isSubmitting}
      onEditorOpenChange={(open) => {
        setEditorOpen(open);
        if (!open) {
          setEditingMemory(null);
          setEditorContent('');
        }
      }}
      onEditorContentChange={setEditorContent}
      onSave={() => {
        void handleSave();
      }}
    />
  );

  if (showInitialLoading) {
    return (
      <EmptyState
        icon={Brain01Icon}
        title={t('loadingTitle')}
        isLoading={true}
      />
    );
  }

  if (showEmptyState) {
    return (
      <>
        <EmptyState
          icon={Brain01Icon}
          iconSize={40}
          iconColor="var(--colors-fg-neutral-disabled)"
          title={t('emptyTitle')}
          description={t('emptyDescription')}
          action={
            <Button size="sm" variant="outline" onClick={openCreate}>
              {tMemories('addMemory')}
            </Button>
          }
        />
        {dialogs}
      </>
    );
  }

  return (
    <VStack h="full" gap="0" w="full" overflow="hidden">
      <Box
        w="full"
        px="3"
        py="2"
        borderBottomWidth="1px"
        borderColor="border.subtle"
      >
        <HStack gap="2" alignItems="center" flexWrap="nowrap" w="full">
          {totalCount > 0 && (
            <MetaDefault color="fg.muted" whiteSpace="nowrap" flexShrink={0}>
              {totalCount}
            </MetaDefault>
          )}

          {showSearch && (
            <Box flex="1" minW={0} position="relative">
              <Search01Icon
                size={10}
                style={{
                  position: 'absolute',
                  left: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--colors-fg-muted)',
                  zIndex: 1,
                }}
              />
              <Input
                size="xs"
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                pl="6"
                h="6"
                w="full"
              />
            </Box>
          )}

          <HStack gap="1" alignItems="center" flexShrink={0} ml="auto">
            <Button
              size="xs"
              variant="outline"
              whiteSpace="nowrap"
              onClick={openCreate}
            >
              <Add01Icon size={14} />
              {tMemories('addMemory')}
            </Button>
          </HStack>
        </HStack>
      </Box>

      <Scroll flex="1">
        <Box p="4" h="full" minH="0">
          <Box flex="1" w="full" h="full" overflow="hidden" minH="400px">
            <Table
              key={`endpoint-memories-table-${searchTerm}`}
              columnDefs={columnDefs}
              rowData={memories}
              rowHeight={44}
              headerHeight={36}
              noBorder={true}
              fontSize={12}
              headerFontSize={11}
              animateRows={false}
              rowBuffer={100}
              debounceVerticalScrollbar={true}
              defaultColDef={{
                sortable: true,
                filter: false,
                resizable: true,
              }}
              suppressCellFocus={true}
              getRowId={(params) => params.data.recordId}
              domLayout="normal"
            />
          </Box>
        </Box>
      </Scroll>

      {dialogs}
    </VStack>
  );
}
